/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { useGetSummaryInsightDashboardQuery } from '@api/insight-dashboards';
import { GridLayoutDashboard } from '../custom-dashboard/GridLayoutDashboard';
import type { GridDashboardItem, OverviewDashboard } from '@g17eco/types/insight-custom-dashboard';
import { AddToOverviewBtn } from './AddToOverviewBtn';
import { useSelector } from 'react-redux';
import { selectMostRecentSurvey } from '../../slice/initiativeSurveyListSlice';
import { useGetSurveyByIdQuery } from '@api/surveys';
import { DashboardSection } from '@g17eco/molecules/dashboard';
import { SdgChart } from '@features/impact-performance/SdgChart';
import { CompanyProfile } from '@features/company-profile';
import { InitiativeData, InitiativeReference } from '@g17eco/types/initiative';
import { SurveyType } from '@g17eco/types/surveyCommon';
import type { DataFilters } from '@routes/custom-dashboard/types';
import { useGridItemContext } from '@features/custom-dashboard/hooks/useGridItemContext';

interface Props {
  initiative: InitiativeData | undefined;
  rootOrg: InitiativeReference | undefined;
  dashboard: OverviewDashboard;
  initiativeId: string;
  canManage: boolean;
  filters: Pick<DataFilters, 'period' | 'surveyType'>;
}

export const Summary = ({ initiative, rootOrg, dashboard, initiativeId, canManage, filters }: Props) => {
  const { data: overview, isFetching: isFetchingOverview } = useGetSummaryInsightDashboardQuery(
    { dashboardId: 'overview', initiativeId, queryParams: { surveyType: SurveyType.Default } },
    { skip: !initiativeId }
  );

  const mostRecentSurvey = useSelector(selectMostRecentSurvey);
  const { data: latestSurvey, isFetching: isFetchingLatestSurvey } = useGetSurveyByIdQuery(
    mostRecentSurvey?._id ?? '',
    {
      skip: !mostRecentSurvey?._id,
    }
  );

  const gridItemContext = useGridItemContext({
    initiativeId,
    dataFilters: filters,
    initiative,
    rootOrg,
    survey: latestSurvey,
    readOnly: true,
    valueLists: dashboard.valueLists,
    surveyUnitConfigs: dashboard.surveyUnitConfigs,
  });

  return (
    <>
      {dashboard.filters.initiativeInfo?.enabled ? <CompanyProfile initiative={initiative} readOnly={false} /> : null}
      {dashboard.filters.sdgContribution?.enabled && dashboard.scorecard ? (
        <DashboardSection>
          <SdgChart initiativeId={initiativeId} scorecard={dashboard.scorecard} />
        </DashboardSection>
      ) : null}
      <GridLayoutDashboard
        isEditing={false}
        gridItems={dashboard.items}
        utrsData={dashboard.utrsData}
        gridItemContext={gridItemContext}
        actionBtn={({ item }: { item: GridDashboardItem }) => {
          if (!canManage) {
            return null;
          }
          return (
            <AddToOverviewBtn
              initiativeId={initiativeId}
              overviewItems={overview?.items ?? []}
              item={item}
              disabled={isFetchingOverview || isFetchingLatestSurvey}
            />
          );
        }}
      />
    </>
  );
};
